import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanFace, Loader2, ShieldAlert } from "lucide-react";
import { useI18n } from "i18n";
import LanguageSwitcher from "components/LanguageSwitcher";
import portalApi, { setPortalToken, clearPortalToken } from "lib/portalApi";

// /pub/enroll — the mandatory face enrollment. The session that lands here carries the
// restricted scope, so this is literally the only page it can use: there is no way into the
// portal without a face on file.
export default function PortalEnroll() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);
  const pollRef = useRef(null);

  const check = useCallback(async () => {
    try {
      const r = await portalApi.get("/me/");
      if (!r.data.face_required) {
        setPortalToken(r.data.token);          // upgraded to the full scope
        clearInterval(pollRef.current);
        navigate("/pub", { replace: true });
      }
    } catch { /* keep waiting */ }
  }, [navigate]);

  useEffect(() => { check(); }, [check]);
  useEffect(() => {
    if (!started) return undefined;
    pollRef.current = setInterval(check, 3000);
    return () => clearInterval(pollRef.current);
  }, [started, check]);

  const start = async () => {
    setBusy(true); setError("");
    try {
      const r = await portalApi.post("/enroll/start/", {});
      setStarted(true);
      window.open(r.data.capture_url, "_blank", "noopener");
    } catch {
      setError(t("pub.enroll.failed", "Não foi possível iniciar a captura."));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <ScanFace className="h-6 w-6 text-primary" />
          <span className="font-semibold tracking-tight">SecFace</span>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button onClick={() => { clearPortalToken(); navigate("/pub/login", { replace: true }); }}
            className="text-sm text-muted-foreground hover:text-foreground">
            {t("pub.signOut", "Sair")}
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 pt-[10vh]">
        <div className="w-full max-w-md text-center">
          <div className="h-14 w-14 rounded-2xl border border-amber-500/40 bg-amber-500/10 grid place-items-center mx-auto mb-4">
            <ShieldAlert className="h-7 w-7 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight mb-2">
            {t("pub.enroll.title", "Cadastre seu rosto")}
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            {t("pub.enroll.body", "Sua conta só fica ativa depois do cadastro facial. É rápido: abra a captura, siga as instruções na tela e volte aqui.")}
          </p>
          {error && <p className="text-sm text-red-500 mb-3">{error}</p>}
          <button type="button" onClick={start} disabled={busy}
            className="w-full h-11 rounded-lg bg-primary text-primary-foreground text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanFace className="h-4 w-4" />}
            {started ? t("pub.enroll.again", "Abrir a captura novamente") : t("pub.enroll.cta", "Abrir a captura")}
          </button>
          {started && (
            <p className="text-xs text-muted-foreground mt-3 inline-flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t("pub.enroll.waiting", "Aguardando a conclusão da captura…")}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
